import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Trash2, Plus } from 'lucide-react';
import { api } from '../lib/api';

const VENDORS = ['grandstream', 'yealink', 'polycom', 'snom', 'fanvil', 'cisco', 'avaya', 'mitel', 'aastra', 'audiocodes', 'linksys'];

export default function PhoneTemplates() {
  const qc = useQueryClient();
  const { data: templates = [] } = useQuery({ queryKey: ['templates'], queryFn: () => api.templates.list() });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [edited, setEdited] = useState<any>(null);
  const { data: active } = useQuery({
    queryKey: ['template', activeId],
    queryFn: () => activeId ? api.templates.get(activeId) : null,
    enabled: !!activeId,
  });

  const cur = edited ?? active;

  const save = useMutation({
    mutationFn: () => activeId ? api.templates.update(activeId, cur) : api.templates.create(cur).then((r: any) => { setActiveId(r.template_uuid); return r; }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); setEdited(null); },
  });
  const remove = useMutation({
    mutationFn: () => api.templates.remove(activeId!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); setActiveId(null); setEdited(null); },
  });

  function newTemplate() {
    setActiveId(null);
    setEdited({ vendor: 'yealink', model: '', filename: '<mac>.cfg', body: '', description: '', enabled: true });
  }

  return (
    <div className="flex gap-4">
      <aside className="w-64 flex-shrink-0">
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold">Templates</h2>
            <button onClick={newTemplate} className="btn-ghost p-1"><Plus size={14} /></button>
          </div>
          <ul className="space-y-1 text-sm">
            {(templates as any[]).map((t: any) => (
              <li key={t.template_uuid}>
                <button onClick={() => { setActiveId(t.template_uuid); setEdited(null); }}
                  className={`w-full text-left px-2 py-1 rounded ${activeId === t.template_uuid ? 'bg-primary text-white' : 'hover:bg-elevated'}`}>
                  <div className="text-xs uppercase opacity-70">{t.vendor}</div>
                  <div>{t.model} / {t.filename}</div>
                </button>
              </li>
            ))}
            {!(templates as any[]).length && <li className="text-textSecondary text-xs">No tenant overrides. Built-in vendor modules apply.</li>}
          </ul>
        </div>
      </aside>
      <div className="flex-1">
        {cur ? (
          <div className="space-y-3">
            <div className="flex gap-3 items-end">
              <div>
                <label className="block text-sm text-textSecondary mb-1">Vendor</label>
                <select className="input" value={cur.vendor} onChange={e => setEdited({ ...cur, vendor: e.target.value })}>
                  {VENDORS.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm text-textSecondary mb-1">Model</label>
                <input className="input" value={cur.model || ''} onChange={e => setEdited({ ...cur, model: e.target.value })} />
              </div>
              <div className="flex-1">
                <label className="block text-sm text-textSecondary mb-1">Filename</label>
                <input className="input font-mono" value={cur.filename || ''} onChange={e => setEdited({ ...cur, filename: e.target.value })} />
              </div>
              <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save</button>
              {activeId && <button onClick={() => remove.mutate()} className="btn-ghost text-danger inline-flex items-center gap-2"><Trash2 size={14} /></button>}
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">Body (use %%placeholder%% for substitution)</label>
              <textarea className="input font-mono text-xs h-[60vh]" value={cur.body_template ?? cur.body ?? ''}
                onChange={e => setEdited({ ...cur, body: e.target.value })} />
            </div>
          </div>
        ) : (
          <div className="text-textSecondary">Select a template or click + to create one.</div>
        )}
      </div>
    </div>
  );
}
